import { useEffect, useState } from 'react'
import type { DrawLottery, NumberLimitHistoryActors } from '@nexbit/types'
import {
  apiAdminGetNumberLimitHistory, apiAdminGetNumberLimitHistoryActors, apiDrawGetLotteries,
} from '@/lib/api'
import { NumberLimitHistoryTable } from '@/components/number-limit-history-table'

/**
 * ประวัติการอั้นเลข (แอดมิน) — every number limit anyone set, the house's and every
 * agent's, in one list.
 *
 * Both in one list rather than two screens on purpose. The question that brings someone
 * here is "ทำไมลูกค้าแทงเลขนี้ไม่ได้", and a number can be limited by the house and by the
 * agent at the same time — so the answer is the two sets of changes in the order they
 * happened, which is only readable if they are in the same table.
 *
 * Read-only. Nothing here changes a limit; ปิดรับเลข is where that is done, and keeping
 * the record separate from the controls means reading the history can never alter it.
 */
export default function AdminNumberLimitHistoryPage() {
  const [lotteries, setLotteries] = useState<DrawLottery[]>([])
  const [actors, setActors] = useState<NumberLimitHistoryActors | null>(null)

  // The two pickers' options, loaded once and separately from the history so a failure
  // here costs a filter and not the list — the list is the page. The actor list in
  // particular barely changes, and refetching it per search would pay a DISTINCT for an
  // answer nobody asked to refresh.
  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const data = await apiDrawGetLotteries()
        if (alive) setLotteries(data)
      } catch {
        // Left empty: the filter is simply not offered, and the history still loads.
      }
    })()
    void (async () => {
      try {
        const data = await apiAdminGetNumberLimitHistoryActors()
        if (alive) setActors(data)
      } catch {
        // ตั้งโดย falls back to แอดมิน / เอเย่นต์ without the individual accounts.
      }
    })()
    return () => { alive = false }
  }, [])

  return (
    <div className="p-4 md:p-6 max-w-7xl">
      <div className="mb-4">
        <h1 className="text-lg font-semibold tracking-tight">ประวัติการอั้นเลข</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          ทุกการปิดรับเลข ตั้งเพดาน และแก้วงเงิน — ทั้งของเว็บและของเอเย่นต์ ค้นหาย้อนหลังตามวันที่
        </p>
      </div>

      <NumberLimitHistoryTable
        fetchPage={apiAdminGetNumberLimitHistory}
        lotteries={lotteries}
        showScope
        actors={actors}
        subtitle="รายการเปลี่ยนแปลง"
        emptyHint="ประวัติจะขึ้นที่นี่เมื่อมีการปิดรับเลข ตั้งเพดานรายเลข แก้วงเงิน หรือเมื่อเอเย่นต์ตั้งกฎของตัวเอง"
      />
    </div>
  )
}
